import { createTheme, colors } from '@mui/material';

// MOH brand palette
const brand = {
  primary: '#004085',
  primaryLight: '#4d7fb8',
  primaryDark: '#002855',
  success: '#28a745',
  successLight: '#5cb85c',
  successDark: '#1e7e34',
  error: '#dc3545',
  warning: '#ffc107',
  info: '#17a2b8',
};

export const createMOHTheme = (mode: 'light' | 'dark') => createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'dark' ? colors.blue[300] : brand.primary,
      light: mode === 'dark' ? colors.blue[200] : brand.primaryLight,
      dark: mode === 'dark' ? colors.blue[400] : brand.primaryDark,
    },
    secondary: {
      main: brand.success,
      light: brand.successLight,
      dark: brand.successDark,
    },
    error: { main: brand.error },
    warning: { main: brand.warning },
    info: { main: brand.info },
    success: { main: brand.success },
    background: {
      // Light mode dimmed slightly — neutral grey gives Paper cards visible
      // separation without the cool blue tint of blueGrey.
      // Between grey[200] (#eeeeee) and grey[300] (#e0e0e0) — slightly dimmed
      // but lighter than 300, gives Paper cards visible separation without
      // feeling heavy.
      default: mode === 'dark' ? colors.grey[900] : '#e8e8e8',
      paper: mode === 'dark' ? colors.grey[800] : colors.common.white,
    },
    ...(mode === 'dark' && {
      divider: 'rgba(255, 255, 255, 0.12)',
    }),
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    // Ensure minimum 16px font size on inputs to prevent iOS zoom
    fontSize: 16,
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.9375rem',
      fontWeight: 500,
      lineHeight: 1.5,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      defaultProps: {
        // Ensure all buttons meet minimum touch target
        disableElevation: false,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 44, // Minimum touch target height
          fontSize: '0.9375rem',
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 20,
          paddingRight: 20,
        },
        sizeSmall: {
          minHeight: 36,
          fontSize: '0.875rem',
          paddingTop: 6,
          paddingBottom: 6,
          paddingLeft: 14,
          paddingRight: 14,
        },
        sizeLarge: {
          minHeight: 48,
          fontSize: '1rem',
          paddingTop: 12,
          paddingBottom: 12,
          paddingLeft: 24,
          paddingRight: 24,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44, // Minimum touch target
          minHeight: 44,
        },
        sizeSmall: {
          minWidth: 36,
          minHeight: 36,
        },
        sizeLarge: {
          minWidth: 48,
          minHeight: 48,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            minHeight: 48, // Ensure adequate touch height
            fontSize: '1rem', // Minimum 16px to prevent iOS zoom
          },
          '& .MuiInputBase-input': {
            fontSize: '1rem',
            padding: '14px',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '1rem', // Minimum 16px
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          height: 'auto',
          minHeight: 32,
        },
        sizeSmall: {
          fontSize: '0.75rem',
          minHeight: 24,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 48, // Touch-friendly menu items
          fontSize: '0.9375rem',
          paddingTop: 12,
          paddingBottom: 12,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 48, // Touch-friendly list items
          paddingTop: 12,
          paddingBottom: 12,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: theme.palette.mode === 'dark'
            ? '0 2px 4px rgba(0,0,0,0.4)'
            : '0 2px 4px rgba(0,0,0,0.1)',
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.grey[900]
            : theme.palette.primary.main,
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.grey[800]
            : theme.palette.common.white,
        }),
      },
    },
  },
});

// Export default light theme for backward compatibility
export const theme = createMOHTheme('light');
